"use client"

import { useRef, useEffect, useCallback } from "react"
import { cn } from "@/lib/utils"

export function InlineInput({
  value,
  onChange,
  placeholder,
  className,
  type = "text",
  variant = "default",
  step,
  min,
  onFocus,
  onBlur,
}: {
  value: string
  onChange: (value: string) => void
  placeholder: string
  className?: string
  type?: string
  variant?: "default" | "light"
  step?: string | number
  min?: string | number
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
}) {
  const isLight = variant === "light"
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={onFocus}
      onBlur={onBlur}
      placeholder={placeholder}
      step={step}
      min={min}
      className={cn(
        "border-0 border-b px-1.5 py-1 h-auto rounded-sm focus:outline-none transition-all duration-200",
        isLight
          ? "border-white/20 hover:border-white/40 focus:border-white bg-transparent text-white placeholder:text-white/50 [color-scheme:dark]"
          : "border-transparent hover:border-border focus:border-ring/50 bg-transparent hover:bg-muted/50 focus:bg-muted/60 text-foreground placeholder:text-muted-foreground/60 print:text-gray-900 print:placeholder:text-gray-400 print:bg-transparent print:border-transparent",
        className
      )}
    />
  )
}

export function InlineTextarea({
  value,
  onChange,
  placeholder,
  className,
  minRows = 2,
}: {
  value: string
  onChange: (value: string) => void
  placeholder: string
  className?: string
  minRows?: number
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current
    if (!textarea) return
    textarea.style.height = "auto"
    const lineHeight = 22
    const minHeight = lineHeight * minRows + 12
    textarea.style.height = `${Math.max(minHeight, textarea.scrollHeight)}px`
  }, [minRows])

  useEffect(() => {
    adjustHeight()
  }, [value, adjustHeight])

  return (
    <textarea
      ref={textareaRef}
      value={value}
      onChange={(e) => {
        onChange(e.target.value)
        adjustHeight()
      }}
      placeholder={placeholder}
      rows={minRows}
      className={cn(
        "w-full border-0 border-b border-transparent hover:border-border focus:border-ring/50 focus:outline-none bg-transparent resize-none px-1.5 py-1.5 rounded-sm transition-all duration-200 placeholder:text-muted-foreground/60 overflow-hidden leading-[22px] hover:bg-muted/50 focus:bg-muted/60 text-foreground print:text-gray-900 print:placeholder:text-gray-400 print:bg-transparent print:border-transparent",
        className
      )}
    />
  )
}
